import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {RenameDesignRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/RenameDesignRequest';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {Design, Project} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Flow} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SheetTabNameValidator} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SpreadsheetTabNameValidator';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {TopMenuState} from '@mintdata-wsp/common/state/ui/visualDesigner/TopMenuState';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Input} from '@mintdata-wsp/wsp/ui/components/forms/Input';
import {IDeleteDialogProps} from '@mintdata-wsp/wsp/ui/dialogs/DeleteDialog';
import {PublishDialogKind} from '@mintdata-wsp/wsp/ui/dialogs/publish/PublishDialog';
import {IRenameDialogProps} from '@mintdata-wsp/wsp/ui/dialogs/RenameDialog';
import {SpreadsheetTabsView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/tabsPanel/SpreadsheetTabsView';
import {SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Point} from '@mintdata/mint-kit/api/models/Point';
import {action, runInAction} from 'mobx';

export class TopMenuController
{
    @action.bound
    public publishAsApplicationHandler():void
    {
        const {openDesign, publish} = controllers.ui;

        publish.showPublishDialog({
            designRef: openDesign.getOpenDesign().designRef,
            kind: PublishDialogKind.Full
        }, true);
    }

    @action.bound
    public async newDesignHandler():Promise<void>
    {
        const project:Project = rootState.ui.mainPage.currentProject;

        const newDesignName:string = await controllers.data.designs.generateNewDesignName();
        const newDesign:Design = await controllers.data.designs.createNewDesign(project, newDesignName);

        controllers.navigation.toDesign(project.id, newDesign.id);
    }

    @action.bound
    public async duplicateDesignHandler():Promise<void>
    {
        const {designId} = controllers.ui.openDesign.getOpenDesign().designRef;
        const project:Project = rootState.ui.mainPage.currentProject;
        const design:Design = rootState.ui.mainPage.currentDesigns.find(({id}) => id === designId);

        const copiedDesign:Design = await controllers.data.designs.duplicateDesign(project, design);

        controllers.navigation.toDesign(project.id, copiedDesign.id);
    }

    private _renameDesignInputRef:Input | null = null;

    @action
    private _setRenameDesignErrorMessage(message:string):void
    {
        rootState.ui.mainPage.renameDesign.errorMessage = message;

        if( this._renameDesignInputRef )
            requestAnimationFrame(() => this._renameDesignInputRef.checkIsValid());
    }

    @action.bound
    public async renameDesignHandler():Promise<void>
    {
        const {designId} = controllers.ui.openDesign.getOpenDesign().designRef;
        const project:Project = rootState.ui.mainPage.currentProject;
        const design:Design = rootState.ui.mainPage.currentDesigns.find(({id}) => id === designId);

        const {dialogs} = controllers.ui;

        dialogs.showDialog(Dialogs.Rename, false, {
            title: 'Rename design',
            bodyText: '',
            defaultName: design.name,
            placeholder: 'Design name',
            isNameValidFn: (name:string) => name && !rootState.ui.mainPage.renameDesign.errorMessage,
            getErrorMessageFn: (name:string) =>
            {
                if( !name )
                    return `Design name can't be empty`;
                else
                    return rootState.ui.mainPage.renameDesign.errorMessage;
            },
            onNameChange: (name:string) => this._setRenameDesignErrorMessage(null),
            onSave: async (name:string) =>
            {
                try
                {
                    this._setRenameDesignErrorMessage(null);

                    const resp:any = await API.designs.rename(project.id, design.id, new RenameDesignRequest(name));

                    if( ApiError.isError(resp) )
                        this._setRenameDesignErrorMessage((resp as ApiError).message);
                    else
                    {
                        runInAction(() =>
                        {
                            dialogs.hideDialog(Dialogs.Rename);
                            design.name = name;
                        });
                    }
                }
                catch( e )
                {
                    this._setRenameDesignErrorMessage(e);
                }

            },
            onInputRef: (ref:Input | null) => this._renameDesignInputRef = ref
        } as IRenameDialogProps);
    }

    @action.bound
    public async importFromDesignHandler():Promise<void>
    {
        const {workbenchVisibility} = controllers.ui;

        workbenchVisibility.setPagesPanelVisibility(true);
        requestAnimationFrame(() => workbenchVisibility.setSurfaceCopyPagesPanelShown(true));
    }

    @action.bound
    public async nameCurrentVersionHandler():Promise<void>
    {
        await controllers.ui.designVersions.refresh();

        controllers.ui.dialogs.showDialog(Dialogs.NameCurrentVersion, false);
    }

    @action.bound
    public showHideVersionHistoryHandler():void
    {
        controllers.ui.designVersions.toggleVersionsPanelVisibility();
    }

    @action.bound
    public undoHandler():void
    {
        controllers.ui.undoRedo.undo();
    }

    @action.bound
    public redoHandler():void
    {
        controllers.ui.undoRedo.redo();
    }

    @action.bound
    public surfaceCutHandler():void
    {

    }

    @action.bound
    public surfaceCopyHandler():void
    {
        controllers.ui.surfaceArea.copyBoxesToClipboard();
    }

    @action.bound
    public surfacePasteHandler():void
    {
        controllers.ui.surfaceArea.pasteBoxesFromClipboard();
    }

    @action.bound
    public surfaceGroupHandler():void
    {
        controllers.ui.surfaceArea.groupSelectedBoxesIntoBox();
    }

    @action.bound
    public surfaceUngroupHandler():void
    {
        controllers.ui.surfaceArea.ungroupSelectedBox();
    }

    @action.bound
    public spreadsheetCutHandler():void
    {
        const {main, workbook, mesh} = controllers.ui.spreadsheets;

        const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(main.getActiveSheet().id);
        const meshState = mesh.getState(ctx);

        if( main.isEditMode || meshState.details.isEditNoteMode )
            return;

        controllers.ui.spreadsheets.meshCopyPaste.copySlotToClipboard(
            ctx.sheetId,
            true,
            ctx.isNested
        );
    }

    @action.bound
    public spreadsheetCopyHandler():void
    {
        const {main, workbook, mesh} = controllers.ui.spreadsheets;

        const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(main.getActiveSheet().id);
        const meshState = mesh.getState(ctx);

        if( main.isEditMode || meshState.details.isEditNoteMode )
            return;

        controllers.ui.spreadsheets.meshCopyPaste.copySlotToClipboard(
            ctx.sheetId,
            false,
            ctx.isNested
        );
    }

    @action.bound
    public spreadsheetPasteHandler():void
    {
        controllers.ui.dialogs.showDialog(Dialogs.ClipboardHint, false);
    }

    @action.bound
    public spreadsheetPasteValuesOnlyHandler():void
    {
        const {main, workbook, meshCopyPaste} = controllers.ui.spreadsheets;

        const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(main.getActiveSheet().id);

        meshCopyPaste.pasteValuesOnly(ctx.sheetId);
    }

    @action.bound
    public newPageHandler():void
    {
        controllers.ui.surfaceArea.addNewPage();
    }

    @action.bound
    public duplicatePageHandler():void
    {
        const {surfaceArea} = controllers.ui;

        const activePage:Page = surfaceArea.getActivePage();

        controllers.ui.surfaceArea.duplicatePage(activePage.id);
    }

    @action.bound
    public renamePageHandler():void
    {
        const {surfaceArea, dialogs} = controllers.ui;

        const activePage:Page = surfaceArea.getActivePage();

        dialogs.showDialog(Dialogs.Rename, false, {
            title: 'Rename page',
            bodyText: 'Enter a new page name:',
            defaultName: activePage.name,
            placeholder: 'Page name',
            isNameValidFn: (name:string) => surfaceArea.isPageNameUnique(activePage.id, name),
            getErrorMessageFn: (name:string) => 'Page name should be unique',
            onSave: (name:string) =>
            {
                if( surfaceArea.renamePage(activePage.id, name) )
                    dialogs.hideDialog(Dialogs.Rename);
            }
        } as IRenameDialogProps);
    }

    @action.bound
    public markAsStartingPageHandler():void
    {
        const {surfaceArea} = controllers.ui;

        const activePage:Page = surfaceArea.getActivePage();

        controllers.ui.surfaceArea.markPageAsStarted(activePage.id);
    }

    @action.bound
    public deletePageHandler():void
    {
        const {surfaceArea, dialogs} = controllers.ui;

        const activePage:Page = surfaceArea.getActivePage();

        dialogs.showDialog(Dialogs.Delete, false, {
            message: 'Are you sure you want to delete this page?',
            onClose: () => dialogs.hideDialog(Dialogs.Delete),
            onClickDelete: () =>
            {
                surfaceArea.removePage(activePage.id);
                dialogs.hideDialog(Dialogs.Delete);
            }
        } as IDeleteDialogProps);
    }

    @action.bound
    public newSheetHandler():void
    {
        const {workbook, main, tabs} = controllers.ui.spreadsheets;

        const workbookId:WorkbookId = main.getRootWorkbook().id;
        const activeSheet:AnySheet = main.getActiveSheet();

        const activeSheetIndex:number = workbook.findSheetIndex(workbookId, activeSheet.id);
        const newSheetIndex:number = activeSheetIndex + 1;
        const newSheetName:string = workbook.getNextSheetName(workbookId);

        const newSheet:AnySheet = workbook.createNewSheet(main.getRootWorkbook(), newSheetName);

        workbook.insertSheet(workbookId, newSheetIndex, newSheet);

        const tabsViewInstance:SpreadsheetTabsView = tabs.getTabsViewInstance(workbookId);

        if( tabsViewInstance )
            tabsViewInstance.activateTab(workbookId, newSheet.id);
    }

    @action.bound
    public duplicateSheetHandler():void
    {
        const {workbook, main, tabs} = controllers.ui.spreadsheets;

        const workbookId:WorkbookId = main.getRootWorkbook().id;
        const activeSheet:AnySheet = main.getActiveSheet();

        const duplicateSheetId:SheetId = workbook.duplicateAndInsertSheet(workbookId, activeSheet.id);
        const tabsViewInstance:SpreadsheetTabsView = tabs.getTabsViewInstance(workbookId);

        if( tabsViewInstance )
            tabsViewInstance.activateTab(workbookId, duplicateSheetId);
    }

    @action.bound
    public renameSheetHandler():void
    {
        const {dialogs} = controllers.ui;
        const {workbook, main, tabs} = controllers.ui.spreadsheets;

        const workbookId:WorkbookId = main.getRootWorkbook().id;
        const activeSheet:AnySheet = main.getActiveSheet();

        dialogs.showDialog(Dialogs.Rename, false, {
            title: 'Rename sheet',
            bodyText: 'Enter a new sheet name:',
            defaultName: activeSheet.name,
            placeholder: 'Sheet name',
            isNameValidFn: (name:string) => SheetTabNameValidator.isValidSheetName(workbookId, activeSheet.id, name),
            getErrorMessageFn: (name:string) =>
            {
                if( SheetTabNameValidator.sheetWithNameExists(workbookId, activeSheet.id, name) )
                    return `A sheet with the name "${name}" already exists. Please enter another name.`;
                else if( SheetTabNameValidator.isInvalidSheetName(name) )
                    return `Sheet name can't be empty`;
            },
            onSave: (name:string) =>
            {
                workbook.renameSheet(workbookId, activeSheet.id, name);

                dialogs.hideDialog(Dialogs.Rename);

                const tabsViewInstance:SpreadsheetTabsView = tabs.getTabsViewInstance(workbookId);

                if( tabsViewInstance )
                    tabsViewInstance.activateTab(workbookId, activeSheet.id);

            }
        } as IRenameDialogProps);
    }

    @action.bound
    public moveLeftSheetHandler():void
    {
        const {workbook, main, tabs} = controllers.ui.spreadsheets;

        const workbookId:WorkbookId = main.getRootWorkbook().id;
        const activeSheet:AnySheet = main.getActiveSheet();

        const index:number = workbook.findSheetIndex(workbookId, activeSheet.id);
        const newIndex:number = index - 1;

        workbook.moveSheet(workbookId, activeSheet.id, newIndex);

        const tabsViewInstance:SpreadsheetTabsView = tabs.getTabsViewInstance(workbookId);

        if( tabsViewInstance )
            tabsViewInstance.activateTab(workbookId, activeSheet.id);
    }

    @action.bound
    public moveRightSheetHandler():void
    {
        const {workbook, main, tabs} = controllers.ui.spreadsheets;

        const workbookId:WorkbookId = main.getRootWorkbook().id;
        const activeSheet:AnySheet = main.getActiveSheet();

        const index:number = workbook.findSheetIndex(workbookId, activeSheet.id);
        const newIndex:number = index + 1;

        workbook.moveSheet(workbookId, activeSheet.id, newIndex);

        const tabsViewInstance:SpreadsheetTabsView = tabs.getTabsViewInstance(workbookId);

        if( tabsViewInstance )
            tabsViewInstance.activateTab(workbookId, activeSheet.id);
    }

    @action.bound
    public deleteSheetHandler():void
    {
        const {workbook, main, tabs} = controllers.ui.spreadsheets;

        const workbookId:WorkbookId = main.getRootWorkbook().id;
        const activeSheet:AnySheet = main.getActiveSheet();

        const tabsViewInstance:SpreadsheetTabsView = tabs.getTabsViewInstance(workbookId);

        controllers.ui.dialogs.showDialog(Dialogs.Delete, false, {
            message: 'Are you sure you want to delete this sheet?',
            onClose: () => controllers.ui.dialogs.hideDialog(Dialogs.Delete),
            onClickDelete: () =>
            {
                controllers.ui.dialogs.hideDialog(Dialogs.Delete);

                let sheetIndex:number = workbook.findSheetIndex(workbookId, activeSheet.id);

                if( sheetIndex === 0 )
                    sheetIndex++;
                else
                    sheetIndex--;

                const newActiveSheet:AnySheet = workbook.findSheetByIndex(workbookId, sheetIndex);

                if( tabsViewInstance )
                    tabsViewInstance.activateTab(workbookId, newActiveSheet.id);

                setTimeout(() =>
                {
                    workbook.removeSheet(workbookId, activeSheet.id);
                });
            }
        } as IDeleteDialogProps);
    }

    @action.bound
    public newCloudFlowHandler():void
    {
        const {projectId, designId} = controllers.ui.openDesign.getOpenDesign().designRef;

        controllers.ui.flow.createFlowAndNavigate(projectId, designId);
    }

    @action.bound
    public flowEditorHandler():void
    {
        const {projectId, designId} = controllers.ui.openDesign.getOpenDesign().designRef;
        const flows:Flow[] = controllers.ui.flow.getFlows();

        if( flows.length )
            controllers.navigation.toFlowEditor(projectId, designId, flows[0].id);
        else
            controllers.ui.flow.createFlowAndNavigate(projectId, designId);
    }

    @action.bound
    public showAllPanelsHandler():void
    {
        const {workbenchVisibility} = controllers.ui;

        workbenchVisibility.setPagesPanelVisibility(true);
        workbenchVisibility.setPropertiesPanelVisibility(true);
        workbenchVisibility.setSpreadsheetVisibility(true);

        if( workbenchVisibility.isSpreadsheetFullScreen() )
            workbenchVisibility.toggleSpreadsheetFullScreen();
    }

    @action.bound
    public hideAllPanelsHandler():void
    {
        const {workbenchVisibility} = controllers.ui;

        workbenchVisibility.setPagesPanelVisibility(false);
        workbenchVisibility.setPropertiesPanelVisibility(false);
        workbenchVisibility.setSpreadsheetVisibility(false);
    }

    @action
    public setTopMenuIsExpanded(value:boolean):void
    {
        const state:TopMenuState = this._getState();

        if( state.isExpandingTransitionInProgress
            || state.isCollapsingTransitionInProgress
            || state.isLogoTransitionInProgress )
            return;

        state.isExpanded = value;

        if( value )
            state.isExpandingTransitionInProgress = true;
        else
            state.isCollapsingTransitionInProgress = true;

        state.isLogoTransitionInProgress = true;
    }

    public isTopMenuExpanded():boolean
    {
        return this._getState().isExpanded;
    }

    public isExpandingTransitionInProgress():boolean
    {
        return this._getState().isExpandingTransitionInProgress;
    }

    @action.bound
    public toggleGridlinesHandler():void
    {
        const {main, workbook, mesh} = controllers.ui.spreadsheets;

        const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(main.getActiveSheet().id);
        const meshState = mesh.getState(ctx);

        meshState.setIsGridlinesVisible(!meshState.isGridlinesVisible);
    }

    public isCollapsingTransitionInProgress():boolean
    {
        return this._getState().isCollapsingTransitionInProgress;
    }

    public isLogoTransitionInProgress():boolean
    {
        return this._getState().isLogoTransitionInProgress;
    }

    @action
    public endTopMenuLogoTransition():void
    {
        this._getState().isLogoTransitionInProgress = false;
    }

    @action
    public endTopMenuTransition():void
    {
        const state:TopMenuState = this._getState();

        state.isExpandingTransitionInProgress = false;
        state.isCollapsingTransitionInProgress = false;
    }

    @action.bound
    public zoomInHandler():void
    {
        const {surfaceArea, workbenchVisibility} = controllers.ui;

        const gridGlassDomRef:HTMLElement | null = workbenchVisibility.getGridGlassDomRef();

        if( !gridGlassDomRef )
            return;

        const clientRect:ClientRect = gridGlassDomRef.getBoundingClientRect();

        const pivot = Point.from(
            clientRect.left + (clientRect.width / 2 ),
            clientRect.top + (clientRect.height / 2 )
        );

        surfaceArea.zoomIn(pivot);
    }

    @action.bound
    public zoomOutHandler():void
    {
        const {surfaceArea, workbenchVisibility} = controllers.ui;

        const gridGlassDomRef:HTMLElement | null = workbenchVisibility.getGridGlassDomRef();

        if( !gridGlassDomRef )
            return;

        const clientRect:ClientRect = gridGlassDomRef.getBoundingClientRect();

        const pivot = Point.from(
            clientRect.left + (clientRect.width / 2 ),
            clientRect.top + (clientRect.height / 2 )
        );

        surfaceArea.zoomOut(pivot);
    }

    @action.bound
    public resetZoomAndPanHandler():void
    {
        controllers.ui.surfaceArea.resetView();
    }

    @action.bound
    public zoomToFitHandler():void
    {
        controllers.ui.surfaceArea.fitPage();
    }

    @action.bound
    public getZoomPercentHandler(percent:number):() => void
    {
        return () => controllers.ui.surfaceArea.zoomTo(percent);
    }

    private _getState():TopMenuState
    {
        return rootState.ui.visualDesigner.topMenuState;
    }
}
